import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { RoleBadge, StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Users as UsersIcon, UserPlus, Lock, Trash2, Mail } from 'lucide-react';
import { FilterBar, SelectFilter, SearchInput, ClearFilters } from '@/components/filters';
import { buildQuery } from '@/lib/filters';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  created_at?: number;
  createdAt?: number;
  custom_role_id?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  last_login?: number | null;
}

interface CustomRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

interface UsersProps {
  user?: any;
}

function Users({ user: currentUser }: UsersProps) {
  const { t } = useTranslation();
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  const [users, setUsers] = useState<User[]>([]);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'MEMBER',
    status: 'ACTIVE',
    organizationId: '',
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchCustomRoles();
    if (isSuperAdmin) fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setTimeout(fetchUsers, search ? 300 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, statusFilter, orgFilter]);

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('all');
    setStatusFilter('all');
    setOrgFilter('all');
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const qs = buildQuery({
        search,
        role: roleFilter,
        status: statusFilter,
        organizationId: orgFilter,
        limit: 1000,
      });

      const response = await fetch(`${apiUrl}/api/users${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data?.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/organizations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // GET /api/organizations is paginated: { data: { organizations: [...] } };
        // tolerate a bare array too.
        const list = data.data?.organizations ?? (Array.isArray(data.data) ? data.data : []);
        setOrgs(list.map((o: any) => ({ id: o.id, name: o.name })));
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomRoles(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch custom roles:', error);
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewUser({ name: '', email: '', role: 'MEMBER', status: 'ACTIVE', organizationId: '' });
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleInviteUser = async (user: User) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/${user.id}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (response.ok) {
        const result = data.data || {};
        const emailedMsg = result.emailed
          ? `An invite email was sent to ${result.email}.`
          : `Email was not sent.`;
        alert(`${emailedMsg}\nTemp password: ${result.tempPassword}`);
        fetchUsers();
      } else {
        alert(data.error || t('common.error'));
      }
    } catch (error) {
      console.error('Failed to invite user:', error);
      alert(t('common.error'));
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Update user role / custom role via the roles endpoint
      const rolesResponse = await fetch(`${apiUrl}/api/roles/user/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: selectedUser.role,
          custom_role_id: selectedUser.custom_role_id,
        }),
      });

      // Update core user fields (name/email/role/status/organization) via the users endpoint
      const userResponse = await fetch(`${apiUrl}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: selectedUser.name,
          email: selectedUser.email,
          role: selectedUser.role,
          status: selectedUser.status,
          organizationId: selectedUser.organization_id,
        }),
      });

      if (rolesResponse.ok && userResponse.ok) {
        setIsEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsDeleteDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const data = await response.json();
        alert(data.error || t('common.error'));
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(t('common.error'));
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      alert(t('users.dialogs.resetPassword.matchError'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          password: resetPasswordData.password,
        }),
      });

      if (response.ok) {
        alert(t('users.dialogs.resetPassword.success'));
        setIsResetPasswordOpen(false);
        setResetPasswordData({ password: '', confirmPassword: '' });
        setSelectedUser(null);
      } else {
        const data = await response.json();
        alert(data.error || t('users.dialogs.resetPassword.error'));
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert(t('users.dialogs.resetPassword.error'));
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser({ ...user });
    setIsEditDialogOpen(true);
  };

  const openResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setResetPasswordData({ password: '', confirmPassword: '' });
    setIsResetPasswordOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Users & access"
        icon={UsersIcon}
        title={t('navigation.users')}
        subtitle={t('users.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          {t('users.createUser')}
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('users.title')}</CardTitle>
          <CardDescription>
            {t('users.description', { count: users.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FilterBar>
            <SearchInput value={search} onChange={setSearch} placeholder="Name or email…" />
            <SelectFilter
              label={t('users.table.systemRole')}
              value={roleFilter}
              onChange={setRoleFilter}
              options={['OWNER', 'ADMIN', 'MEMBER', 'VIEWER', 'SUPER_ADMIN'].map((v) => ({ value: v, label: v }))}
            />
            <SelectFilter
              label={t('users.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              options={['ACTIVE', 'INACTIVE', 'INVITED', 'DISABLED'].map((v) => ({ value: v, label: v }))}
            />
            {isSuperAdmin && (
              <SelectFilter
                label="Organization"
                value={orgFilter}
                onChange={setOrgFilter}
                options={orgs.map((o) => ({ value: o.id, label: o.name }))}
              />
            )}
            <ClearFilters onClick={clearFilters} />
          </FilterBar>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.userName')}</TableHead>
                <TableHead>{t('users.email')}</TableHead>
                <TableHead>{t('users.table.systemRole')}</TableHead>
                <TableHead>{t('users.table.customRole')}</TableHead>
                <TableHead>{t('users.status')}</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Last login</TableHead>
                <TableHead>{t('licenses.createdAt')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const customRole = customRoles.find(r => r.id === user.custom_role_id);
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <RoleBadge role={user.role} />
                      </TableCell>
                      <TableCell>
                        {customRole ? (
                          <Badge variant="outline">{customRole.name}</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">{t('users.dialogs.assignRoles.none')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={user.status} />
                      </TableCell>
                      <TableCell>{user.organization_name || '—'}</TableCell>
                      <TableCell>
                        {user.last_login ? formatDate(user.last_login) : 'Never'}
                      </TableCell>
                      <TableCell>
                        {formatDate(user.created_at || user.createdAt)}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleInviteUser(user)}
                          aria-label={`Invite ${user.name}`}
                          title={user.status === 'INVITED' ? `Resend invite to ${user.name}` : `Invite ${user.name}`}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        {currentUser?.role === 'SUPER_ADMIN' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openResetPasswordDialog(user)}
                              aria-label={`${t('users.resetPassword')} ${user.name}`}
                              title={`${t('users.resetPassword')} ${user.name}`}
                            >
                              <Lock className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => openDeleteDialog(user)}
                              aria-label={`${t('common.delete')} ${user.name}`}
                              title={`${t('common.delete')} ${user.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.createUser')}</DialogTitle>
            <DialogDescription>
              {t('users.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('users.userName')}</Label>
              <Input
                id="name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('users.email')}</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">{t('users.role')}</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OWNER">{t('users.owner')}</SelectItem>
                  <SelectItem value="ADMIN">{t('users.admin')}</SelectItem>
                  <SelectItem value="MEMBER">{t('users.member')}</SelectItem>
                  <SelectItem value="VIEWER">{t('users.viewer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">{t('users.status')}</Label>
              <Select
                value={newUser.status}
                onValueChange={(value) => setNewUser({ ...newUser, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                  <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin && (
              <div className="grid gap-2">
                <Label htmlFor="organization">Organization</Label>
                <Select
                  value={newUser.organizationId || ''}
                  onValueChange={(value) => setNewUser({ ...newUser, organizationId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateUser} disabled={!newUser.name || !newUser.email}>
              {t('users.createUser')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.dialogs.assignRoles.title')}</DialogTitle>
            <DialogDescription>
              {t('users.dialogs.assignRoles.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('users.userName')}</Label>
                <Input
                  id="edit-name"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  disabled
                />
                <p className="text-xs text-muted-foreground">{t('users.dialogs.assignRoles.nameCannotChange')}</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-email">{t('users.email')}</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                  disabled
                />
                <p className="text-xs text-muted-foreground">{t('users.dialogs.assignRoles.emailCannotChange')}</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-role">{t('users.table.systemRole')}</Label>
                <Select
                  value={selectedUser.role}
                  onValueChange={(value) => setSelectedUser({ ...selectedUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OWNER">{t('users.owner')}</SelectItem>
                    <SelectItem value="ADMIN">{t('users.admin')}</SelectItem>
                    <SelectItem value="MEMBER">{t('users.member')}</SelectItem>
                    <SelectItem value="VIEWER">{t('users.viewer')}</SelectItem>
                    <SelectItem value="USER">{t('users.user')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-custom-role">{t('users.dialogs.assignRoles.customRoleOptional')}</Label>
                <Select
                  value={selectedUser.custom_role_id || 'none'}
                  onValueChange={(value) => setSelectedUser({
                    ...selectedUser,
                    custom_role_id: value === 'none' ? null : value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('users.dialogs.assignRoles.none')}</SelectItem>
                    {customRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name} ({role.permissions.length} {t('roles.table.permissions').toLowerCase()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('users.dialogs.assignRoles.customRoleInfo')}
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">{t('users.status')}</Label>
                <Select
                  value={selectedUser.status}
                  onValueChange={(value) => setSelectedUser({ ...selectedUser, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                    <SelectItem value="INVITED">INVITED</SelectItem>
                    <SelectItem value="DISABLED">DISABLED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin && (
                <div className="grid gap-2">
                  <Label htmlFor="edit-organization">Organization</Label>
                  <Select
                    value={selectedUser.organization_id || ''}
                    onValueChange={(value) => setSelectedUser({ ...selectedUser, organization_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {orgs.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditUser}>
              {t('users.dialogs.assignRoles.assignRolesButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.dialogs.resetPassword.title')}</DialogTitle>
            <DialogDescription>
              {t('users.dialogs.resetPassword.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-password">{t('users.dialogs.resetPassword.newPassword')}</Label>
              <Input
                id="new-password"
                type="password"
                value={resetPasswordData.password}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, password: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">{t('users.dialogs.resetPassword.confirmPassword')}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={resetPasswordData.confirmPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleResetPassword} disabled={!resetPasswordData.password || !resetPasswordData.confirmPassword}>
              {t('users.resetPassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.delete')} {t('users.title').slice(0, -1)}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}?
              This action cannot be undone if the user has no licenses. If they have licenses, they will be disabled instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Users;
